import React from 'react';
import { Redirect } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  DisplayText,
  IconLink,
  IllusLocation,
  LoadingPlaceholder,
  Modal,
  ModalDialogue,
  theme,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useCartHandoffMachine, useCartHandoffContent } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const CartHandoffModal = () => {
  const isFeatureEnabled = useFeatureFlag('CELS-5511-cart-handoff-enabled');

  if (!isFeatureEnabled) return <Redirect href="/" />;

  return <CartHandoffModalEnabled />;
};

const CartHandoffModalEnabled = () => {
  const { formatMessage } = useIntl();

  // ───────────────────────────────────────────────────────────────────────────

  const {
    conflicts,
    isDismissed,
    isInActiveBagWarning,
    isInConflictsWarning,
    isNothingAvailable,
    isHandingOffCart,
    triggerHandoff,
    dismissHandoff,
  } = useCartHandoffMachine();

  const { title, body, cta } = useCartHandoffContent({
    isDismissed,
    isHandingOffCart,
    isNothingAvailable,
    isInConflictsWarning,
    isInActiveBagWarning,
    conflicts,
  });

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      size="medium"
      imageSrc={IllusLocation}
      visible={!isDismissed}
      accessibilityLabel={formatMessage(messages.a11yLabel)}
    >
      <View style={styles.content}>
        <Modal.Row style={styles.titleContainer}>
          <DisplayText sizeMatch={['24']}>{title}</DisplayText>

          {isHandingOffCart ? null : (
            <IconLink
              name="IconClose"
              palette="muted"
              width={32}
              height={32}
              iconSize={20}
              aria-label={formatMessage(messages.close)}
              onPress={dismissHandoff}
            />
          )}
        </Modal.Row>

        <Modal.Row>
          {body ? (
            <BodyText sizeMatch={['16']}>{body}</BodyText>
          ) : (
            <LoadingPlaceholder rowHeight={48} palette="cream" />
          )}
        </Modal.Row>

        <Modal.Footer withoutTopBorder>
          <Button
            palette="primary"
            size="large"
            accessibilityRole="button"
            aria-label={cta}
            isLoading={isHandingOffCart}
            onPress={isNothingAvailable ? dismissHandoff : triggerHandoff}
          >
            {cta}
          </Button>
        </Modal.Footer>
      </View>
    </ModalDialogue>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Cart Handoff Dialogue',
    description: 'Cart Handoff | A11y Label',
  },
  close: {
    defaultMessage: 'Close',
    description: 'Cart Handoff | Close',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flex: 1,
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
});
